import { Link } from "react-router-dom";
import { Clock, Heart } from "lucide-react";

interface Category {
  id: string;
  name: string;
  slug: string;
}

interface RecentPost {
  slug: string;
  title: string;
  published_at: string | null;
}

interface BlogSidebarProps {
  categories: Category[];
  recentPosts: RecentPost[];
  activeCategory?: string | null;
  onCategoryChange?: (slug: string | null) => void;
}

const BlogSidebar = ({ categories, recentPosts, activeCategory, onCategoryChange }: BlogSidebarProps) => {
  return (
    <aside className="space-y-6">
      {/* Categories */}
      <div className="rounded-2xl border border-border bg-card p-5 shadow-[0_8px_30px_rgba(0,0,0,0.08)]">
        <h3 className="mb-4 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">
          <span className="h-1 w-4 rounded-full bg-primary" />
          Categorias
        </h3>
        <ul className="space-y-1">
          <li>
            <button
              onClick={() => onCategoryChange?.(null)}
              className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm transition-all ${
                !activeCategory
                  ? "bg-primary text-primary-foreground font-semibold shadow-sm"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground"
              }`}
            >
              Todas
              {!activeCategory && <span className="h-1.5 w-1.5 rounded-full bg-primary-foreground" />}
            </button>
          </li>
          {categories.map((cat) => (
            <li key={cat.id}>
              <button
                onClick={() => onCategoryChange?.(cat.slug)}
                className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm transition-all ${
                  activeCategory === cat.slug
                    ? "bg-primary text-primary-foreground font-semibold shadow-sm"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`}
              >
                {cat.name}
                {activeCategory === cat.slug && <span className="h-1.5 w-1.5 rounded-full bg-primary-foreground" />}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Recent Posts */}
      {recentPosts.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-5 shadow-[0_8px_30px_rgba(0,0,0,0.08)]">
          <h3 className="mb-4 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">
            <span className="h-1 w-4 rounded-full bg-primary" />
            Posts Recentes
          </h3>
          <ul className="divide-y divide-border">
            {recentPosts.map((post) => (
              <li key={post.slug} className="py-3 first:pt-0 last:pb-0">
                <Link to={`/blog/${post.slug}`} className="group block">
                  <p className="text-sm font-medium leading-snug text-foreground transition-colors group-hover:text-primary line-clamp-2">
                    {post.title}
                  </p>
                  {post.published_at && (
                    <span className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {new Date(post.published_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })}
                    </span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* CTA */}
      <div className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/5 via-card to-primary/10 p-6 text-center shadow-[0_8px_30px_rgba(0,0,0,0.08)]">
        <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
          <Heart className="h-5 w-5 text-primary" />
        </div>
        <p className="text-sm font-semibold text-foreground mb-1">Quer apoiar o IESSA?</p>
        <p className="text-xs text-muted-foreground mb-4">Sua contribuição fortalece a missão.</p>
        <Link to="/contato#apoie" className="inline-block rounded-full bg-primary px-5 py-2.5 text-xs font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 transition-all hover:shadow-md">
          Quero apoiar
        </Link>
      </div>
    </aside>
  );
};

export default BlogSidebar;
